import React, { useEffect, useState } from 'react';
import { Quote, ArrowUpRight } from 'lucide-react';
import { fetchCaseStudies } from '../../lib/api';

const FALLBACK_CASES = [
  {
    id: 'account-expansion',
    tag: 'ABM · Account Expansion',
    industry: 'IT Services · US',
    title: 'Strategic account expansion for a leading US IT services provider.',
    challenge: 'Penetrate hard-to-access markets. Client sought to expand into Europe and Canada, targeting a select list of strategic accounts with limited prior traction.',
    solution: 'Built the ABM list from intent + tech-install signals, identified director-and-above IPCs via LinkedIn, ran GDPR-compliant programmatic + LinkedIn Ads across CA/EU, personalised tele-outreach using engagement scores.',
    kpis: [
      { label: 'Total investment', value: '$83.85K' },
      { label: 'Revenue ratio', value: '~1%' },
      { label: 'Return multiple', value: '100\u00d7' },
      { label: 'Markets', value: 'EU + CA' },
    ],
    highlight: 'The client\u2019s $83,850 investment amounted to ~1% of the revenue generated \u2014 detailed profiling & personalised engagement turned it into a substantial return.',
    tools: ['Predictor', 'Intello', 'Innovator'],
  },
  {
    id: 'live-event',
    tag: 'Live Event · Trade Show',
    industry: 'UK · Processing & Packaging',
    title: 'One week, one thousand registrations \u2014 UK\u2019s biggest trade show.',
    challenge: 'A leading UK trade show contacted us 7\u201310 days before the event, falling short of attendance goals, with a 15,000-contact suppression list to navigate.',
    solution: 'Multi-channel blitz: LinkedIn + programmatic display for reach, multi-wave speaker-led email campaign, SMS reminders, telemarketing for personal touch, post-registration nurture sequence to ensure show-up.',
    kpis: [
      { label: 'Timeline', value: '7\u201310 days' },
      { label: 'Registrations', value: '~1,000' },
      { label: 'Email share', value: '73%' },
      { label: 'Conv. rate', value: '18.2%' },
    ],
    highlight: 'Speaker-voiced nurture emails and precision retargeting drove attendees to a landing page in a compressed 5-day sprint.',
    tools: ['Email', 'LinkedIn', 'Tele', 'SMS', 'Display'],
  },
  {
    id: 'bant',
    tag: 'BANT · Enterprise Demand Gen',
    industry: 'IT Management · US',
    title: 'BANT-qualified pipeline for an AI-powered IT platform.',
    challenge: 'Accelerate lead generation across the US market for both a Core IT platform and a new AI-enhanced product line \u2014 with strict $250K+ budget qualification for AI prospects.',
    solution: 'Built a 116,300-contact universe across 20,600 target companies, deployed BANT-scripted SDRs, ran a 15-day multi-channel program with strong QA gates on every batch.',
    kpis: [
      { label: 'BANT leads', value: '68' },
      { label: 'Vs. target', value: '+20%' },
      { label: 'Email open', value: '42.8%' },
      { label: 'Call connect', value: '42.1%' },
    ],
    highlight: 'Delivered 68 fully-qualified BANT leads with 85% retention from initial qualification to final QA \u2014 exceeding the 56-lead target by 20%.',
    tools: ['Email', 'Tele', 'LinkedIn', 'BANT'],
  },
  {
    id: 'webinar',
    tag: 'Webinar · Multi-Region',
    industry: 'CX/EX SaaS · Global',
    title: 'Enterprise IT leaders across 7 global markets \u2014 in one webinar.',
    challenge: 'Simultaneously penetrate seven markets across three continents, targeting IT Managers-and-above at 500+ employee companies, within a compressed 5-week window.',
    solution: '25,000-contact multi-region database, five-phase email campaign, 8-agent telemarketing team, LinkedIn Sponsored + InMail with dayparting, tri-regional live delivery.',
    kpis: [
      { label: 'Registrations', value: '1,119' },
      { label: 'Vs. target', value: '112%' },
      { label: 'Attendance', value: '41%' },
      { label: 'Engaged mins', value: '20,253' },
    ],
    highlight: '112% of the 1,000-registration target with an 80% UK/EU attendance rate and an average of 44 minutes of engagement per attendee.',
    tools: ['Email', 'Tele', 'LinkedIn'],
  },
];

export default function CaseStudy() {
  const [cases, setCases] = useState(FALLBACK_CASES);
  const [active, setActive] = useState(FALLBACK_CASES[0].id);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchCaseStudies()
      .then((data) => {
        if (!alive || !Array.isArray(data) || data.length === 0) return;
        setCases(data);
        setActive(data[0].id);
      })
      .catch(() => {
        /* keep fallback silently */
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const c = cases.find((x) => x.id === active) || cases[0];
  if (!c) return null;

  return (
    <section className="py-20 lg:py-32 bg-[#0F1C40] text-[#F1EDE4]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 mb-10 lg:mb-12 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">
              <span className="w-6 h-px bg-[#4ECDC4]" />
              <span>Featured Case Studies</span>
            </div>
            <h2 className="text-3xl lg:text-5xl xl:text-6xl tracking-tight leading-[1.05] font-medium">
              {cases.length === 1 && loading ? (
                <>Loading <span className="font-serif italic text-[#4ECDC4]">case studies…</span></>
              ) : (
                <>Four programs, <span className="font-serif italic text-[#4ECDC4]">four measurable outcomes.</span></>
              )}
            </h2>
            <p className="mt-4 text-white/70 max-w-2xl">
              Real campaigns across ABM, live events, BANT programs, and multi-region webinars. Each story below is a snapshot of what our teams and platforms deliver.
            </p>
          </div>
        </div>

        {/* Case tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10">
          {cases.map((cs) => (
            <button
              key={cs.id}
              onClick={() => setActive(cs.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors duration-200 flex items-center gap-2 ${
                active === cs.id ? 'border-[#4ECDC4] text-[#F1EDE4]' : 'border-transparent text-white/50 hover:text-white/80'
              }`}
            >
              <span className={`text-[10px] font-mono ${active === cs.id ? 'text-[#4ECDC4]' : 'text-white/40'}`}>{String(cases.indexOf(cs) + 1).padStart(2, '0')}</span>
              <span>{cs.tag}</span>
            </button>
          ))}
        </div>

        {/* Active case detail */}
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="bg-[#12234C] border border-white/10 rounded-2xl p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4] bg-[#4ECDC4]/10 px-2 py-1 rounded-full border border-[#4ECDC4]/30">
                  {c.industry}
                </span>
              </div>
              <h3 className="text-2xl lg:text-4xl font-serif italic leading-tight mb-6">{c.title}</h3>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF6B35] mb-2">The Challenge</div>
                  <p className="text-[14px] text-white/75 leading-relaxed">{c.challenge}</p>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-2">Our Solution</div>
                  <p className="text-[14px] text-white/80 leading-relaxed">{c.solution}</p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center gap-3">
                <span className="text-[10px] font-mono uppercase tracking-widest text-white/50">Channels & tools</span>
                {c.tools.map((t) => (
                  <span key={t} className="px-3 py-1 rounded-full text-xs bg-white/5 border border-white/10">{t}</span>
                ))}
              </div>
            </div>

            {/* Quote / takeaway */}
            <div className="mt-6 bg-gradient-to-br from-[#4ECDC4]/15 to-transparent border border-[#4ECDC4]/25 rounded-2xl p-8 flex items-start gap-4">
              <Quote className="w-7 h-7 text-[#4ECDC4] shrink-0" />
              <p className="text-lg font-serif italic leading-snug text-[#F1EDE4]">{c.highlight}</p>
            </div>
          </div>

          {/* KPI stack */}
          <div className="lg:col-span-4 space-y-3">
            {c.kpis.map((k, i) => (
              <div key={k.label} className="bg-[#12234C] border border-white/10 rounded-2xl p-5 flex items-baseline justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">{k.label}</div>
                  <div className="text-3xl lg:text-4xl font-serif italic tabular text-[#4ECDC4]">{k.value}</div>
                </div>
                <span className="text-[10px] font-mono text-white/30">KPI 0{i + 1}</span>
              </div>
            ))}

            <a href="#contact" className="mt-4 inline-flex items-center gap-2 bg-[#4ECDC4] text-[#0F1C40] pl-5 pr-4 py-3 rounded-full text-sm font-semibold hover:bg-white transition-colors w-fit">
              Get a program like this
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
